import { useState } from 'react';

import Box from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Drawer, { DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import InfoIcon from '@liquidweb-dev/undercurrent/icons/lucide/Info';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import toast from '@liquidweb-dev/undercurrent/toast';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { LWApiPublicNetworkIpListPublicResultRaw } from '@liquidweb-dev/club-sauce/public/network/ip/index.raw';
import { LWApiPublicServerAuthDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/server/auth/index.raw';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import { thirdpartyClassnames } from 'components/common/ThirdpartyExclude';
import LWForm from 'components/LWForm';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useNetworkIpListPublic from 'modules/queries/network/ip/useListPublic';
import useServerAuthDetails from 'modules/queries/server/auth/useDetails';
import useServerAuthUpdate from 'modules/queries/server/auth/useUpdate';
import isPort from 'validator/es/lib/isPort';
import {
	HookedSubmit,
	HookedTextField,
	HookedTextFieldPassword,
} from '../Form';

export const Loader = () => (
	<>
		<DrawerContent>
			<Skeleton variant="rounded" sx={{ height: '220px' }} />
			<Skeleton variant="rounded" sx={{ height: '312px' }} />
		</DrawerContent>
		<DrawerFooter sx={{ display: 'flex', flexDirection: 'column', gap: 100 }}>
			<Skeleton variant="rounded" sx={{ height: (theme) => theme.size.lg }} />
			<Skeleton variant="rounded" sx={{ height: (theme) => theme.size.lg }} />
		</DrawerFooter>
	</>
);

interface SupportAccessFormProps {
	onClose: () => void;
	publicIps?: LWApiPublicNetworkIpListPublicResultRaw['items'];
	serverAuthDetails?: LWApiPublicServerAuthDetailsResultRawI;
	uniqId: string;
}

const SupportAccessForm = ({
	onClose,
	publicIps,
	serverAuthDetails,
	uniqId,
}: SupportAccessFormProps) => {
	const {
		special_instructions: initialSpecialInstructions,
		alternate_ssh_ip: initialSshIpRestrictions,
		alternate_ssh_port: initialSshPort,
		login_username: initialSshUsername,
	} = serverAuthDetails || {};

	const { data: assetDetails } = useAssetDetails({
		uniq_id: uniqId,
		alsowith: hostingDetailsAlsowith,
	});

	const { capabilities, isWindows } = assetDetails || {};
	const command = isWindows ? '' : 'SSH ';
	const isCpanel = fromApiBoolean(capabilities?.cpanelLogin);

	const [formValues, setFormValues] = useState({
		alternate_ssh_ip: initialSshIpRestrictions || '',
		alternate_ssh_port: initialSshPort?.toString() || '',
		login_password: '',
		login_username: initialSshUsername || '',
		password: '',
		special_instructions: initialSpecialInstructions || '',
		whm_access_key: '',
	});
	const [formErrors, setFormErrors] = useState({});

	const { mutate: updateServerAuth, isLoading: isUpdatingServerAuth } =
		useServerAuthUpdate();

	const handleSubmit = () => {
		const {
			alternate_ssh_ip,
			alternate_ssh_port,
			login_password,
			login_username,
			password,
			special_instructions,
			whm_access_key,
		} = formValues;

		updateServerAuth(
			{
				alternate_ssh_ip,
				alternate_ssh_port,
				login_password,
				login_username,
				password,
				special_instructions,
				uniq_id: uniqId,
				whm_access_key,
			},
			{
				onSuccess: () => {
					onClose();
					toast.success('Successfully saved support access details');
				},
				onError: (error) => {
					error.errors?.forEach(({ field }) => {
						if (field && field in formValues) {
							setFormErrors((prev) => ({
								...prev,
								[field]: 'This field has an invalid value',
							}));
						}
					});
				},
			},
		);
	};

	return (
		<LWForm
			values={formValues}
			onValuesChange={setFormValues}
			errors={formErrors}
			onErrorsChange={setFormErrors}
			onSubmit={handleSubmit}
			validators={{
				alternate_ssh_port: (value) => {
					if (!value) return '';

					return isPort(value.toString()) ? '' : 'Not a valid port';
				},
			}}
		>
			<DrawerContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 300 }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 200 }}>
						<Box sx={{ display: 'flex', gap: 100, alignItems: 'center' }}>
							<Typography fontWeight={500} variant="title-xs">
								Current Credentials
							</Typography>
							<Tooltip
								arrow
								variant="fancy"
								title="What is this?"
								message="This updates the password our support team has on file for this server, it
			does not change your server password"
							>
								<IconButton variant="barebones" size="large">
									<InfoIcon />
								</IconButton>
							</Tooltip>
						</Box>

						<Typography variant="body-md">
							In order to provide you with support, we may need to log into your
							server. Keeping us up-to-date with login information allows us to
							be proactive and minimize delays in supporting you.
						</Typography>

						<Typography component="i" variant="body-sm">
							If you have forgotten your password or need assistance changing
							it, please contact our Support team.
						</Typography>
					</Box>
					<HookedTextFieldPassword
						name="password"
						label={isWindows ? 'Administrator password' : 'Root password'}
						placeholder="Current password"
						slotProps={{
							input: { className: thirdpartyClassnames.join(' ') },
						}}
					/>
					<Typography fontWeight={500} variant="body-md" textAlign="center">
						or
					</Typography>
					<HookedTextField
						name="login_username"
						label={`Alternative ${command}username`}
						placeholder="Username"
						slotProps={{
							htmlInput: {
								minLength: 2,
								maxLength: 20,
							},
						}}
					/>
					<HookedTextFieldPassword
						name="login_password"
						label={`Alternative ${command}password`}
						placeholder="Password"
						slotProps={{
							input: { className: thirdpartyClassnames.join(' ') },
							htmlInput: {
								minLength: 6,
							},
						}}
					/>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 300 }}>
					<Typography fontWeight={500} variant="title-xs">
						Optional additional info
					</Typography>
					<HookedTextField
						name="alternate_ssh_ip"
						select
						label={`${command}IP restrictions`}
						slotProps={{ select: { displayEmpty: true } }}
					>
						<MenuItem value="">No restrictions</MenuItem>
						{publicIps?.map(({ ip }) => {
							if (!ip) return null;

							return (
								<MenuItem key={ip} value={ip}>
									{ip}
								</MenuItem>
							);
						})}
					</HookedTextField>
					<HookedTextField
						name="alternate_ssh_port"
						label={command ? `${command}port` : 'Port'}
						placeholder="Port number"
					/>
					{isCpanel && (
						<HookedTextField
							name="whm_access_key"
							multiline
							label="WHM access key"
							placeholder="Paste key here"
							minRows={4}
							slotProps={{
								input: { className: thirdpartyClassnames.join(' ') },
							}}
						/>
					)}
					<HookedTextField
						name="special_instructions"
						multiline
						label="Special instructions"
						placeholder="Add any instructions here"
						minRows={4}
						slotProps={{
							input: { className: thirdpartyClassnames.join(' ') },
						}}
					/>
				</Box>
			</DrawerContent>
			<DrawerFooter sx={{ display: 'flex', flexDirection: 'column', gap: 100 }}>
				<HookedSubmit
					variant="primary"
					loading={isUpdatingServerAuth}
					size="large"
				>
					Submit
				</HookedSubmit>
				<Button variant="ghost" onClick={onClose} size="large">
					Cancel
				</Button>
			</DrawerFooter>
		</LWForm>
	);
};

export interface DrawerSupportAccessProps extends DrawerProps {
	onClose: () => void;
	uniqId: string;
	open: boolean;
}

const DrawerSupportAccess = ({
	onClose,
	uniqId,
	open,
	...drawerProps
}: DrawerSupportAccessProps) => {
	const { data: publicIpsData, isLoading: publicIpsIsLoading } =
		useNetworkIpListPublic(
			{
				uniq_id: uniqId,
				expand_ips: 1,
				page_size: 9999,
			},
			{ enabled: Boolean(open) },
		);

	const { data: serverAuthDetailsData, isLoading: serverAuthDetailsIsLoading } =
		useServerAuthDetails(
			{
				uniq_id: uniqId,
			},
			{ enabled: Boolean(open) },
		);

	return (
		<Drawer anchor="right" onClose={onClose} open={open} {...drawerProps}>
			<DrawerHeader onClose={onClose}>Support access</DrawerHeader>
			{publicIpsIsLoading || serverAuthDetailsIsLoading ? (
				<Loader />
			) : (
				<SupportAccessForm
					publicIps={publicIpsData?.items}
					serverAuthDetails={serverAuthDetailsData}
					uniqId={uniqId}
					onClose={onClose}
				/>
			)}
		</Drawer>
	);
};

export default DrawerSupportAccess;
